import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { fetchAllData } from '../../utils/fetchAllData'
import { okxApiEndpoint } from '../constants'
import { createPrivateGetHeaders } from '../utils'
import type { Withdrawal } from './schema'
import { withdrawalSchema } from './schema'

const responseSchema = z.object({
  code: z.string(),
  msg: z.string(),
  data: z.array(withdrawalSchema),
})

type FetchAllWithdrawalHistoriesParameters = {
  readonly apiKey: string
  readonly apiSecret: string
  readonly password: string
  readonly from: number
  readonly to: number
  readonly proxy?: string
}

const urlPath = '/api/v5/asset/withdrawal-history'

export const fetchAllWithdrawalHistories = ({
  apiKey,
  apiSecret,
  password,
  from,
  to,
  proxy,
}: FetchAllWithdrawalHistoriesParameters): Promise<readonly Withdrawal[]> =>
  fetchAllData({
    from,
    to,
    limit: 100,
    desc: true,
    idKey: 'wdId',
    timestampKey: 'ts',
    interval: 200,
    fetchData: async ({ from, to, limit, desc }) => {
      if (!desc) {
        throw new Error('desc must be true')
      }
      if (limit > 100) {
        throw new Error('limit must be less than or equal to 100')
      }

      const url = new URL(urlPath, okxApiEndpoint)
      url.searchParams.append('type', '4')
      url.searchParams.append('limit', limit.toString())
      if (from !== undefined) {
        url.searchParams.append('before', from.toString())
      }
      if (to !== undefined) {
        url.searchParams.append('after', to.toString())
      }
      const query = url.searchParams.toString()
      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: await createPrivateGetHeaders({
              apiKey,
              apiSecret,
              password,
              urlPath: url.pathname,
              query: query.length > 0 ? `?${query}` : '',
            }),
          },
          schema: responseSchema,
        },
      )

      return response.data
    },
  })
